import { useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import {
  useInfiniteNotifications,
  useMarkAllAsRead,
  useNotificationDrawerActions,
} from '@/features/notification/hooks/hooks'
import { useSidebarStore } from '@/features/sidebar/store/sidebarStore'
import { cn } from '@/utils'
import { DEFAULT_SEARCH_REQUEST_BODY } from '@/utils/constants'

import { NotificationFilterType, NotificationStatus } from '../../types'

import { DrawerHeader } from './DrawerHeader'
import { NotificationList } from './NotificationList'

const NOTIFICATION_TABS: Array<{
  key: NotificationStatus
  labelKey: string
}> = [
  { key: NotificationStatus.Unread, labelKey: 'notifications.tabs.unread' },
  { key: NotificationStatus.Read, labelKey: 'notifications.tabs.read' },
]

export const NotificationDrawer = () => {
  const {
    isOpen,
    activeTab,
    filterType,
    isMuted,
    isPinned,
    closeDrawer,
    setActiveTab,
    toggleMute,
    togglePin,
  } = useNotificationDrawerActions()
  const { isSidebarCollapsed } = useSidebarStore()
  const { t } = useTranslation()
  const { mutate: markAllAsRead } = useMarkAllAsRead()
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const sidebarWidth = isSidebarCollapsed ? 64 : 296

  // Build filter_by object based on activeTab and filterType
  const filterBy = useMemo(() => {
    const filter: {
      notification_type?: string[]
      status?: NotificationStatus[]
    } = {}

    // Add status filter based on active tab
    if (activeTab === NotificationStatus.Unread) {
      filter.status = [NotificationStatus.Unread]
    } else if (activeTab === NotificationStatus.Read) {
      filter.status = [NotificationStatus.Read]
    }

    // Add notification_type filter based on filterType
    if (filterType !== NotificationFilterType.All) {
      filter.notification_type = [filterType]
    }

    return filter
  }, [activeTab, filterType])

  // Fetch notifications with infinite scroll
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteNotifications(
      {
        limit: DEFAULT_SEARCH_REQUEST_BODY.limit ?? 20,
        sort_by: '-created_at',
        filter_by: filterBy,
      },
      {
        enabled: isOpen,
      }
    )

  // Flatten all pages into a single array of notifications
  const notifications = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data]
  )

  // Handle scroll to load more notifications
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || !hasNextPage || isFetchingNextPage) {
      return
    }

    const container = scrollContainerRef.current
    const scrollTop = container.scrollTop
    const scrollHeight = container.scrollHeight
    const clientHeight = container.clientHeight

    // Load more when user scrolls to 80% of the content
    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const tabClass = (key: NotificationStatus) =>
    `
      px-1 py-2 cursor-pointer text-sm font-semibold bg-transparent border-r-0
      ${
        activeTab === key
          ? 'text-brand-secondary-700 border-b-2 border-blue-700'
          : 'text-quaternary-500 border-transparent hover:text-brand-secondary-700'
      }
    `

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop overlay - only show when unpinned */}
      {!isPinned && (
        <div
          className="fixed inset-0 z-30 bg-black/50 transition-opacity duration-300"
          onClick={closeDrawer}
          aria-hidden="true"
        />
      )}

      {/* Drawer */}
      <div
        className={cn(
          'fixed inset-y-0 flex flex-col',
          'w-[400px] h-full',
          'bg-white border-r border-secondary',
          'transition-all duration-300 ease-in-out',
          isPinned ? 'z-10' : 'z-40 shadow-xl',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        style={{ left: `${sidebarWidth}px` }}
      >
        <DrawerHeader
          isMuted={isMuted}
          isPinned={isPinned}
          toggleMute={toggleMute}
          togglePin={togglePin}
          markAllAsRead={markAllAsRead}
          closeDrawer={closeDrawer}
        />

        <div className="flex-shrink-0 pt-1 gap-3 flex px-4">
          {NOTIFICATION_TABS.map((tab) => (
            <button
              key={tab.key}
              className={tabClass(tab.key)}
              onClick={() => setActiveTab(tab.key)}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </div>

        <div
          className="flex-1 overflow-y-auto p-2 border-t border-secondary"
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
          {isLoading && notifications.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-quaternary-500">
              {t('notifications.loading', 'Loading notifications...')}
            </div>
          ) : (
            <NotificationList
              notifications={notifications}
              activeTab={activeTab}
              isPinned={isPinned}
              isFetchingNextPage={isFetchingNextPage}
            />
          )}
        </div>
      </div>
    </>
  )
}
